import React, { useState, useEffect, useRef, Fragment } from 'react'
import PropTypes from 'prop-types'
import tw, { css, styled } from 'twin.macro'
import { cx } from '@emotion/css'
import { Link, graphql } from 'gatsby'
import Img from 'gatsby-image'
import { Disclosure, Dialog, Transition } from '@headlessui/react'
import {
	ChevronDown,
	Filter,
	CheckSquare,
	PauseCircle,
	PlayCircle,
} from 'react-feather'
import { SwiperComponent } from '../components/pool-site-quote/MaytronicsBuildSwiperComponent'
import BuildLogo from '../images/Maytronics-PSQ_LockUp.png'
import BuildLogoFooter from '../images/Maytronics-PSQ_LockUp_DARK.png'

import Layout from '../components/layout'
import Meta from '../components/meta'
import WhyMaytronicsTable from '../components/pool-site-quote/WhyMaytronicsAccordion'
import { FeatureAccordion } from '../components/pool-site-quote/FeatureAccordion'
import CRM from '../images/CRM_Icon.svg'
import SiteViewer from '../images/SiteViewer_Icon.svg'
import ThreeDCalculations from '../images/3DCal_Icon.svg'
import Customisation from '../images/Customisation_Icon.svg'
import CustomerEngagement from '../images/CustomerEngagement_Icon.svg'
import QuoteContract from '../images/Quote&Contract_Icon.svg'
import Dashboard from '../images/Dashboard_Icon.svg'
import DocumentManagement from '../images/Docuement_Icon.svg'
import Gallery from '../images/Gallery_Icon.svg'
import Shop from '../images/Shop_Icon.svg'
import ContactModal from '../components/pool-site-quote/ContactModal'

/**
 * Elite Dealer - Page Componenet
 * @param {object} props - component props
 * @param {{}} props.data - static query data
 * @param {object} props.location - browser location api
 * @param {string} props.location.search - url params
 * @returns {JSX.Element} Elite Builder page
 * @see EliteBuilderPageQuery
 */
const MaytronicsBuildPage = props => {
	const {
		Testimonial1,
		Testimonial2,
		Testimonial3,
		Testimonial4,
		Testimonial5,
		FeaturesImage,
		SpasaImage,
		videoUrl,
		WhyMaytronicsImage,
		DesktopBanner,
		EliteBuilderIconBlack,
		EliteBuilderIconWhite,
		pageData,
		formData,
	} = props.data
	const {
		seoTitle,
		seoKeywords,
		seoDescription,
		node_locale,
		slug,
	} = pageData.edges[0].node
	const [isScrolled, setIsScrolled] = useState(false)
	const [control, setControl] = useState(true)

	useEffect(() => {
		if (typeof window !== 'undefined') {
		const handleScroll = () => {
			if (window.scrollY > 20) {
				setIsScrolled(true)
			} else {
				setIsScrolled(false)
			}
		}

		window.addEventListener('scroll', handleScroll)
		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}
	}, [])

	const testimonials = [
		{
			img: Testimonial1.nodes[0],
		},
		{
			img: Testimonial2.nodes[0],
		},
		{
			img: Testimonial3.nodes[0],
		},
		{
			img: Testimonial4.nodes[0],
		},
		{
			img: Testimonial5.nodes[0],
		},
	]

	const feature1 = [
		{
			title: 'CRM',
			content:
				'Save customer details, allocate to sales execs, set status, add notes and track history to manage your leads.',
			svg: CRM,
		},
		{
			title: 'SITE VIEWER & DESIGN',
			content:
				'Lookup sites in hi-res imagery to view site planning information and measure access. Site a pool design over an air photo, select from or create design templates, and import house plans and site surveys to scale and create customised designs.',
			svg: SiteViewer,
		},
		{
			title: '3D CALCULATIONS',
			content:
				'3D calculations for any shaped pool, including water surface area, perimeter, water volume, interior surface area and excavation volume - all the calculations you need to prepare an accurate quote.',
			svg: ThreeDCalculations,
		},
		{
			title: 'CUSTOMISATION',
			content:
				'Save your company profile, logo and colour scheme to produce quotes reflecting your brand.',
			svg: Customisation,
		},
		{
			title: 'CUSTOMER ENGAGEMENT',
			content:
				'Send your quote online via SMS and email, allowing customers to approve or respond online.',
			svg: CustomerEngagement,
		},
	]
	const feature2 = [
		{
			title: 'QUOTE & CONTRACT',
			content:
				'Prepare professional-looking quotes, save template quotes and auto-complete contracts.',
			svg: QuoteContract,
		},
		{
			title: 'DASHBOARD',
			content: 'View chart status and map the location of all leads.',
			svg: Dashboard,
		},
		{
			title: 'DOCUMENT MANAGEMENT',
			content:
				'Manage and store any lead documents, drawings, files and site photos.',
			svg: DocumentManagement,
		},
		{
			title: 'GALLERY',
			content:
				'Store pool and spa equipment brochures, finished images, and site photos.',
			svg: Gallery,
		},
		{
			title: 'SHOP',
			content:
				'Order site documents and professional services to streamline your pool approvals. We manage the whole process for you.',
			svg: Shop,
		},
	]

	const play = useRef(null)
	const playVidHandler = () => {
		setControl(false)
		play.current.play()
	}
	const pauseVidHandler = () => {
		setControl(true)
		play.current.pause()
	}

	const [showModal, setShowModal] = useState(false)
	const [subject, setSubject] = useState(null)

	const handleModal = _subject => {
		setSubject(_subject)
		setShowModal(true)
	}

	return (
		<Layout>
			<Meta
				title={seoTitle}
				description={seoDescription}
				keywords={seoKeywords}
				lang={node_locale}
			/>
			<div
				tw="w-screen relative"
				css={css`
					background: #062026;
				`}
			>
				{/* NAV BAR */}
				<div
					tw="fixed w-full h-16 md:h-20 z-40 border-white"
					css={css`
						background: ${isScrolled ? '#062026' : 'transparent'};
						border-bottom: ${isScrolled
							? '1px solid white'
							: '1px solid transparent'};
						@media (min-width: 1024px) {
							background: #062026;
							border-bottom: 2px solid white;
						}
					`}
				>
					<div tw="max-w-screen-xl mx-auto h-full px-2 md:px-5 py-4">
						<div tw="w-full h-full flex justify-between">
							<img
								src={BuildLogo}
								alt="Pool Site Quote"
								css={tw`hover:cursor-pointer`}
								onClick={() => {
									if (typeof window !== 'undefined') {
										window.scrollTo({ top: 0, behavior: 'smooth' })
									}
								}}
							/>

							<div tw="h-full flex items-center justify-end gap-1 md:gap-4">
								<button
									onClick={() => handleModal('Contact Us')}
									tw="uppercase text-2xs md:text-xs text-white border-2 rounded-3xl border-white font-semibold py-1 px-2 md:py-2 md:px-4 hover:bg-blue-600"
								>
									<span tw='whitespace-nowrap'>Contact Us</span>
								</button>
								<div tw='pb-2 w-8 md:w-10 md:py-2'>
									<Link to='/'>
									<div tw='md:p-1 border-2 border-transparent hover:border-white hover:bg-blue-500 hover:cursor-pointer rounded-md hover:bg-opacity-75 hover:border-opacity-50'>
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="white">
										<path stroke-linecap="round" stroke-linejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
									</svg>
									</div>
									</Link>
									</div>
							</div>
						</div>
					</div>
				</div>
				<div tw="w-full absolute" css={css``}>
					<Img
						{...DesktopBanner.nodes[0]}
						tw="w-full"
						loading="eager"
						css={css`
							object-fit: contain;
						`}
					/>
				</div>
				<section tw="w-screen relative z-10" css={css``}>
					<div
						tw="max-w-screen-xl px-8 sm:px-20 lg:px-28 xl:px-8 mx-auto xl:grid grid-cols-5 text-white py-8 md:py-24 relative"
						css={css`
							min-height: 300px;
							@media (min-width: 1280px) {
								height: 80vh;
								min-height: 650px;
								max-height: 750px;
							}
						`}
					>
						<div tw="h-full flex flex-col text-center pt-16 md:pt-10 xl:py-20 col-span-2">
							<h1 tw="text-2xl lg:text-5xl mt-4 text-center">
								<span tw="font-bold text-3xl md:text-6xl leading-8 md:leading-none lg:leading-6">
									Ultimate
									<br tw="hidden md:block" /> All-in-One
									<br />
								</span>{' '}
								Pool Builder App
							</h1>
							<p tw="md:text-xl mt-12 md:mt-4 mb-4">
								Your secret weapon for success.
							</p>
							<button
								onClick={() => handleModal('Request a Demo')}
								tw="mx-auto uppercase text-2xs md:text-xs border-2 rounded-3xl font-semibold py-1 px-4 md:py-2 md:px-4 hover:bg-blue-600"
								css={css`
									border-color: white;
									transition: background 0.3s ease-in-out;
									&:hover {
										background: #58c8df;
										color: #ffffff;
										border-color: #58c8df;
									}
								`}
							>
								Request a demo
							</button>
						</div>
						<div
							tw="col-span-3 h-full p-10 relative hidden md:block"
							css={css`
								@media (min-width: 1280px) {
									max-height: 480px;
								}
							`}
						>
							<div tw="border border-gray-300 rounded-2xl h-full w-full bg-black p-10 relative">
								<div tw="h-full w-full relative">
									{/* Content */}

									<video ref={play} tw="w-full h-full object-contain" loop>
										<source
											src={videoUrl?.nodes[0]?.file?.url}
											type="video/mp4"
										></source>
									</video>
									{control ? (
										<div tw="hover:cursor-pointer absolute top-1/2 left-1/2 -ml-20 -mt-24 z-20 transform -translate-x-1/2 -translate-y-1/2">
											<PlayCircle
												onClick={playVidHandler}
												className="play"
												color="#fff"
												width="170"
												height="170"
												opacity="0.6"
											/>
										</div>
									) : (
										<div tw="hover:cursor-pointer absolute bottom-0 mb-2 right-0">
											<PauseCircle
												onClick={pauseVidHandler}
												className="pause"
												color="#9999"
												width="70"
												height="70"
												opacity="0.6"
											/>
										</div>
									)}
								</div>
								<div tw="absolute bottom-2 left-1/2 transform -translate-x-1/2 -ml-6 h-5 w-14 rounded-full border border-gray-200">
									{/* BUTTON IMAGE */}
								</div>
								<div
									tw="absolute -bottom-44 -ml-10 rounded-2xl h-40 w-full"
									style={{
										padding: '1px',
										background:
											'linear-gradient(to bottom, rgba(200, 200, 200, 0.2), rgba(200, 200, 200, 0))',
									}}
								>
									<div
										tw="h-full w-full p-10 pb-0 rounded-2xl"
										style={{
											background:
												'linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0))',
										}}
									>
										<div
											tw="absolute top-2 left-1/2 transform -translate-x-1/2 -ml-6 h-5 w-14 rounded-full"
											style={{
												padding: '1px',
												background:
													'linear-gradient(to bottom, rgba(200, 200, 200, 0.2), rgba(200, 200, 200, 0))',
											}}
										>
											<div
												tw="h-full w-full rounded-full"
												style={{
													background:
														'linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4))',
												}}
											></div>
											{/* BUTTON IMAGE */}
										</div>
										<div
											tw="h-full w-full"
											style={{
												background:
													'linear-gradient(to bottom, rgba(97, 133, 155, 0.7), rgba(97, 133, 155, 0))',
											}}
										></div>
										{/* Water Reflection */}
									</div>
								</div>
							</div>
							<div tw="absolute bottom-12 -left-10 transform -translate-x-1/2 w-28">
								<Img
									{...SpasaImage.nodes[0]}
									tw="w-full"
									loading="eager"
									css={css`
										img {
											object-fit: contain !important;
										}
									`}
								/>
							</div>
						</div>
					</div>
				</section>
				<section tw="z-10 relative max-w-screen-lg mx-auto h-full text-white py-5 sm:py-14 xl:py-14 md:mt-10 xl:mt-0 text-center px-6 sm:px-20">
					<h3 tw="md:text-2xl font-bold md:mb-4">
						REVOLUTIONISE HOW YOU BUILD & SELL
					</h3>
					<p tw="text-sm md:text-lg leading-5 sm:leading-8 px-4 md:px-12">
						Streamline every step of your sales process with{' '}
						<strong>Pool Site Quote</strong>. From managing leads to designing
						stunning pools and generating precise quotes, this all-in-one app
						makes selling faster, easier, and more impactful. Elevate your
						business, impress your clients, and close more{' '}
						<span tw="whitespace-nowrap">deals—effortlessly.</span>
					</p>
				</section>
				<section tw="relative z-10 max-w-screen-lg mx-auto h-full text-white text-center mt-6 md:mt-0 md:px-20">
					<div tw="w-full">
						<Img
							{...FeaturesImage.nodes[0]}
							tw="w-full"
							loading="eager"
							css={css`
								img {
									transform: scale(1.5);
									@media (min-width: 767px) {
										border-radius: 30px;
										transform: scale(1);
									}
								}
							`}
						/>
					</div>
					<p tw="text-sm md:text-lg font-semibold leading-5 sm:leading-8 pt-8 px-6 md:px-12">
						Are you ready to transform your pool business?
						<br />
						Discover how the features of{' '}
						<span tw="text-blue-500">Pool Site Quote</span> can elevate your
						business success.
					</p>
				</section>
				<section tw="relative z-10 max-w-screen-md mx-auto h-full text-white text-center px-10 lg:px-6 md:px-20 sm:px-12 py-6 md:pt-10 md:pb-16 md:grid grid-cols-2 gap-4">
					<div tw="w-full hidden md:block">
						<FeatureAccordion items={feature1} />
					</div>
					<div tw="w-full hidden md:block">
						<FeatureAccordion items={feature2} />
					</div>
					<div tw="w-full md:hidden">
						<FeatureAccordion items={[...feature1, ...feature2]} />
					</div>
				</section>
				<section tw="relative z-10 max-w-screen-lg mx-auto h-full text-white text-center md:px-20 mt-4 md:mt-0">
					<div tw="w-full">
						<Img
							{...WhyMaytronicsImage.nodes[0]}
							tw="w-full"
							loading="eager"
							css={css`
								img {
									transform: scale(1.2);
									@media (min-width: 767px) {
										border-radius: 30px;
										transform: scale(1);
									}
								}
							`}
						/>
					</div>
					<h3 tw="md:text-xl font-semibold mb-4 md:text-blue-500 px-6 md:px-10 lg:px-0 pt-8">
						Award-Winning Intuitive All-in-One Sales Solution Designed for Pool
						Builders.
					</h3>
					<p tw="text-sm md:text-lg leading-5 sm:leading-8 px-5 lg:px-20">
						Streamline quoting, site planning, and approvals with
						industry-leading tools, expert guidance, and unmatched efficiency.
					</p>
					<p tw="text-sm md:text-lg mt-4 md:mt-5 leading-5 sm:leading-8 px-5 md:px-0">
						See how{' '}
						<span tw="md:text-blue-500 font-semibold">Pool Site Quote</span>{' '}
						compares below.
					</p>
				</section>

				<section tw="relative z-10 max-w-screen-lg mx-auto mt-4 md:mt-0">
					<WhyMaytronicsTable />
				</section>

				<div tw="w-full flex justify-center md:mt-4">
					<button
						onClick={() => handleModal('Request a Demo')}
						tw="text-white text-center mx-auto uppercase text-2xs md:text-xs border-2 rounded-3xl font-semibold px-4 py-1 md:py-2 md:px-4 hover:bg-blue-500"
						css={css`
							border-color: white;
							transition: background 0.3s ease-in-out;
							&:hover {
								background: #58c8df;
								color: #ffffff;
								border-color: #58c8df;
							}
						`}
					>
						Request a demo
					</button>
				</div>

				<section id="testimonials" css={tw`flex flex-col`}>
					<div css={tw`shadow-2xl py-10 md:py-16 lg:px-0 px-5 md:px-3`}>
						<h3 tw="text-2xl sm:text-4xl font-bold text-center text-white">
							TESTIMONIALS
						</h3>
						<div tw="px-10 md:px-0">
							<hr tw="border-white border max-w-screen-md mb-5 sm:mb-12 " />
						</div>
						<div
							className="builder-swiper-container"
							css={tw`max-w-screen-md mx-auto text-white px-10 md:px-0`}
						>
							<div tw="h-full relative">
								<SwiperComponent data={testimonials} />
							</div>

							<h3 tw="text-2xl sm:text-4xl font-bold text-center text-white mt-14">
								READY TO
								<br tw="md:hidden" /> GET STARTED?
							</h3>
							<hr tw="border-white border max-w-screen-md mt-6 md:mt-0 mb-2 md:mb-12 " />
							<p tw="text-xs md:text-xl leading-5 sm:leading-8 text-center text-white md:px-8 lg:px-0">
								Save time and money with{' '}
								<span tw="text-blue-600 font-semibold">Pool Site Quote.</span>{' '}
								Our intuitive pool builder software
								<br tw="hidden lg:block" /> will transform your planning,
								design, and selling
							</p>
							<div tw="w-full flex justify-center mt-4">
								<button
									onClick={() => handleModal('Start a free trial')}
									tw="text-white text-center mx-auto uppercase text-2xs md:text-xs border-2 rounded-3xl font-semibold  py-1 px-6 md:py-2 md:px-4 hover:bg-blue-500"
									css={css`
										border-color: white;
										transition: background 0.3s ease-in-out;
										&:hover {
											background: #58c8df;
											color: #ffffff;
											border-color: #58c8df;
										}
									`}
								>
									Start a free trial
								</button>
							</div>
						</div>
					</div>
				</section>
				{/* OWN FOOTER */}
				<div tw="w-full h-20 md:h-32 bg-blue-500 border-t md:border-t-4 border-white">
					<div tw="max-w-screen-xl mx-auto h-full px-5 py-6 md:px-8 md:py-10">
						<div tw="w-full h-full flex justify-between">
							<img
								src={BuildLogoFooter}
								alt="Pool Site Quote"
								css={tw`hover:cursor-pointer`}
								onClick={() => {
									if (typeof window !== 'undefined') {
										window.scrollTo({ top: 0, behavior: 'smooth' })
									}
								}}
							/>
							<div tw="h-full flex items-center justify-end">
								<button
									onClick={() => handleModal('Contact Us')}
									tw="uppercase text-2xs md:text-xs border-2 rounded-3xl font-semibold  py-1 px-2 md:py-2 md:px-4"
									css={css`
										border-color: #004d66;
										background: #004d66;
										color: white;
										transition: background 0.3s ease-in-out;
										&:hover {
											background: #ffffff;
											color: black;
											border-color: white;
										}
									`}
								>
									Contact Us
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Additional Section End */}

			<ContactModal
				showModal={showModal}
				setShowModal={setShowModal}
				subject={subject}
				setSubject={setSubject}
			/>
		</Layout>
	)
}

MaytronicsBuildPage.propTypes = {
	props: PropTypes.shape({
		data: PropTypes.shape({
			Testimonial1: PropTypes.object,
			Testimonial2: PropTypes.object,
			Testimonial3: PropTypes.object,
			Testimonial4: PropTypes.object,
			Testimonial5: PropTypes.object,
			FeaturesImage: PropTypes.object,
			SpasaImage: PropTypes.object,
			videoUrl: PropTypes.object,
			WhyMaytronicsImage: PropTypes.object,
			DesktopBanner: PropTypes.object,
			EliteBuilderIconBlack: PropTypes.object,
			EliteBuilderIconWhite: PropTypes.object,
			pageData: PropTypes.object,
			formData: PropTypes.object,
		}),
		location: PropTypes.shape({
			search: PropTypes.string,
		}),
	}),
}

export default MaytronicsBuildPage

/**
 * Page content query
 * @private
 * @static
 * @returns {() => void}
 */
export const MaytronicsBuildPageQuery = graphql`
					query MaytronicsBuildPageQuery {
						Testimonial5: allContentfulAsset(
							filter: {
								contentful_id: { eq: "HKmkAHMXqyT0eAFbIh4mD" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 700, maxHeight: 700, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						Testimonial4: allContentfulAsset(
							filter: {
								contentful_id: { eq: "3Ql878y1Hntl29Yj0KhVE3" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 700, maxHeight: 700, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						Testimonial3: allContentfulAsset(
							filter: {
								contentful_id: { eq: "cWPiO3MT3FwFlMWS6DY33" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 700, maxHeight: 700, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						Testimonial2: allContentfulAsset(
							filter: {
								contentful_id: { eq: "5xYFwrt4N9yf14B5U9dokn" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 700, maxHeight: 700, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						Testimonial1: allContentfulAsset(
							filter: {
								contentful_id: { eq: "K3zJ0A10f8tlWbTZz3shC" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 700, maxHeight: 700, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						FeaturesImage: allContentfulAsset(
							filter: {
								contentful_id: { eq: "4tfVKVaYjY7LPqU7NnQXFB" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 800, maxHeight: 300, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						SpasaImage: allContentfulAsset(
							filter: {
								contentful_id: { eq: "2bB9847QdDfZnwe5OLtgYf" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 650, maxHeight: 730, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						videoUrl: allContentfulAsset(
							filter: {
								contentful_id: { eq: "4qDNJRHlyEGchGgLfmpMAI" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								file {
									url
								}
							}
						}
						WhyMaytronicsImage: allContentfulAsset(
							filter: {
								contentful_id: { eq: "3yMhCY11wH6KzZZ2M1LG4s" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 800, maxHeight: 300, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						DesktopBanner: allContentfulAsset(
							filter: {
								contentful_id: { eq: "7xBPVgOgWCUM9xaoWJSAya" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 800, maxHeight: 800, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						EliteBuilderIconBlack: allContentfulAsset(
							filter: {
								contentful_id: { eq: "2FGI9eyHF0xM3xbd4eLObn" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 400, maxHeight: 140, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						EliteBuilderIconWhite: allContentfulAsset(
							filter: {
								contentful_id: { eq: "7EqIQcXpeDvtS1ZLNX0Jsl" }
								node_locale: { eq: "en-AU" }
							}
						) {
							nodes {
								title
								alt: description
								fluid(maxWidth: 400, maxHeight: 140, quality: 90) {
									...GatsbyContentfulFluid_withWebp
								}
							}
						}
						pageData: allContentfulPage(
							filter: {
								contentful_id: { eq: "2H2HJgKLggOCBzE1qYLENU" }
								node_locale: { eq: "en-AU" }
							}
						) {
							edges {
								node {
									seoTitle
									seoKeywords
									seoDescription
									node_locale
									slug
									body {
										json
									}
								}
							}
						}
						formData: allContentfulForm(
							filter: { contentful_id: { eq: "24Hf9D9CjQvHmHWXG88pxu" } }
						) {
							edges {
								node {
									title
									description
									endpoint
									node_locale
									items {
										... on ContentfulFormField {
											helpText
											inputAutocomplete
											inputId
											inputName
											inputPlaceholder
											inputRequired
											inputType
											textareaCols
											textareaRows
											icon
											validationText
											width
											options
											node_locale
											label
										}
										... on ContentfulFormGroup {
											title
											items {
												width
												validationText
												textareaRows
												textareaCols
												options
												node_locale
												label
												inputType
												inputRequired
												inputPlaceholder
												inputName
												inputId
												inputAutocomplete
												icon
												helpText
											}
										}
									}
								}
							}
						}
					}
				`
export const InfoItem = props => (
	<Disclosure tw="pb-4">
		{({ open }) => (
			<>
				<Disclosure.Button tw="px-8 cursor-pointer">
					<ChevronDown tw="mx-auto h-8 w-8" />
				</Disclosure.Button>

				{/*
        Use the Transition + open render prop argument to add transitions.
      */}
				<Transition
					show={open}
					enter={cx(tw`transition duration-100 ease-out`)}
					enterFrom={cx(tw`transform scale-95 opacity-0`)}
					enterTo={cx(tw`transform scale-100 opacity-100`)}
					leave={cx(tw`transition duration-75 ease-out`)}
					leaveFrom={cx(tw`transform scale-100 opacity-100`)}
					leaveTo={cx(tw`transform scale-95 opacity-0`)}
				>
					{/*
          Don't forget to add `static` to your Disclosure.Panel!
        */}
					<Disclosure.Panel tw="px-10 pb-8" static>
						{props.description}
					</Disclosure.Panel>
				</Transition>
			</>
		)}
	</Disclosure>
)
